import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Flower, Camera, BookOpen, Palette, Home, Clock, CreditCard, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Campamento de Semana Santa | Gándara International School",
    description: "Campamento de Semana Santa en Gondomar para niños de 3 a 10 años. Actividades al aire libre, arte y naturaleza en Gándara.",
  },
  en: {
    title: "Easter Camp | Gándara International School",
    description: "Easter camp in Gondomar for children aged 3-10. Outdoor activities, art and nature at Gándara.",
  },
  pt: {
    title: "Acampamento de Páscoa | Gándara International School",
    description: "Acampamento de Páscoa em Gondomar para crianças de 3 a 10 anos. Atividades ao ar livre, arte e natureza na Gándara.",
  },
  fr: {
    title: "Camp de Pâques | Gándara International School",
    description: "Camp de Pâques à Gondomar pour enfants de 3 à 10 ans. Activités de plein air, art et nature à Gándara.",
  },
};

const EasterCamp = () => {
  const { language } = useLanguage();
  const t = translations[language].easterCamp;
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />
      
      <div className={`${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <Flower className="w-16 h-16 text-primary mx-auto mb-6" />
              <h1 className="text-5xl font-light text-primary mb-6">
                {t.title}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t.subtitle}
              </p>
            </div>

            {/* Gallery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 overflow-hidden"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Camera className="w-8 h-8 text-primary" />
                {t.gallery.title}
              </h2>
              
              {/* Main image */}
              <div className="relative aspect-video rounded-xl overflow-hidden mb-4 bg-muted">
                <OptimizedPicture 
                  src={t.gallery.images[0].src} 
                  alt={t.gallery.images[0].alt} 
                  width={800}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              </div>
              
              {/* Secondary images grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <OptimizedPicture 
                    src={t.gallery.images[1].src} 
                    alt={t.gallery.images[1].alt} 
                    width={400}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                  <OptimizedPicture 
                    src={t.gallery.images[2].src} 
                    alt={t.gallery.images[2].alt} 
                    width={400}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground text-center mt-4">
                {t.gallery.caption}
              </p>
            </motion.div>

            {/* Description Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-primary" />
                {t.description.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t.description.content1}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t.description.content2}
              </p>
            </motion.div>

            {/* Activities Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Palette className="w-8 h-8 text-primary" />
                {t.activities.title}
              </h2>
              <ul className="space-y-3">
                {t.activities.list.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Learning Spaces Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Home className="w-8 h-8 text-primary" />
                {t.spaces.title}
              </h2>
              <p className="text-muted-foreground mb-6">{t.spaces.intro}</p>
              <ol className="space-y-4">
                {t.spaces.list.map((space: { title: string; description: string }, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1 text-sm">
                      {index + 1}
                    </span>
                    <div>
                      <span className="font-medium text-foreground">{space.title}</span>
                      <p className="text-muted-foreground mt-1">{space.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </motion.div>

            {/* Schedule Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-primary" />
                {t.schedule.title}
              </h2>
              <ol className="space-y-2">
                {t.schedule.list.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
                      {index + 1}
                    </span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ol>
            </motion.div>

            {/* Prices Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-primary" />
                {t.prices.title}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b">
                      <th className="py-3 px-2"></th>
                      <th className="py-3 px-2 text-foreground">{t.prices.headers.fullCamp}</th>
                      <th className="py-3 px-2 text-foreground">{t.prices.headers.singleDay}</th>
                      <th className="py-3 px-2 text-foreground">{t.prices.headers.earlyBird}</th>
                      <th className="py-3 px-2 text-foreground">{t.prices.headers.lunch}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 px-2 font-medium text-foreground">{t.prices.students}</td>
                      <td className="py-3 px-2 text-muted-foreground">75€</td>
                      <td className="py-3 px-2 text-muted-foreground">25€</td>
                      <td className="py-3 px-2 text-muted-foreground">3€</td>
                      <td className="py-3 px-2 text-muted-foreground">8€</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 font-medium text-foreground">{t.prices.external}</td>
                      <td className="py-3 px-2 text-muted-foreground">100€</td>
                      <td className="py-3 px-2 text-muted-foreground">35€</td>
                      <td className="py-3 px-2 text-muted-foreground">4€</td>
                      <td className="py-3 px-2 text-muted-foreground">10€</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* CTA Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 text-center"
            >
              <h2 className="text-2xl font-light text-primary mb-4">
                {t.contact.title}
              </h2>
              <Button
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language])}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-lg text-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl flex items-center gap-3 mx-auto w-fit"
              >
                <MessageCircle className="w-5 h-5" />
                {t.contact.button}
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EasterCamp;
